import { useEffect, useState } from "react";
import type { ScheduleDestinationDTO, ScheduleDTO, UserDTO } from "../../shared/types";
import { useUser } from "../App";
import { get } from "../lib/api";
import { deliveryWords, destinationFor, othersOf, type DestinationDraft } from "../lib/schedule-destinations";
import { Dialog } from "./Dialog";
import { Icon } from "./Icon";
import { MultiSelect } from "./MultiSelect";
import { Switch } from "./Switch";
import { TextLink } from "./TextLink";
import { Button, FieldGroup, inputCls, timeAgo } from "./ui";

const CHANNELS_MAX = 10;

/** The people an admin's schedule may name: current people, never an agent, a service account or someone who left. */
function usePeople(enabled: boolean): UserDTO[] {
  const [people, setPeople] = useState<UserDTO[]>([]);
  useEffect(() => {
    if (enabled) get<UserDTO[]>("/api/users").then((all) => setPeople(all.filter((p) => p.kind === "person" && !p.retiredAt)), () => setPeople([]));
  }, [enabled]);
  return people;
}

const Caption = ({ children }: { children: string }) => <div className="mb-1.5 text-xs font-medium text-muted">{children}</div>;

/**
 * "Where the answer goes" in the schedule dialog (AIT-55). The owner's own web chat is fixed: the run is a turn there.
 * A member adds their own Slack DM, as before; an admin names people's Slack DMs, people's web chats and Slack channels
 * by id (the app cannot list them). `saved` is the schedule's list as stored, for who has not used Slack with Pandito.
 * `ownChat` false: a schedule that runs as the agent itself (AIT-56), which has no owner's chat, so nothing is fixed
 * and the person setting it up adds themselves if they want it.
 */
export function DestinationsField({ draft, saved, ownChat = true, onChange }: { draft: DestinationDraft; saved: ScheduleDestinationDTO[]; ownChat?: boolean; onChange: (draft: DestinationDraft) => void }) {
  const me = useUser();
  const admin = me.role === "admin" || !ownChat;
  const people = usePeople(admin);
  const fixed = (
    <p className="flex items-center gap-2 text-sm">
      <Icon name="chat" className="size-4 text-muted" /> Your web chat <span className="text-xs text-faint">always</span>
    </p>
  );
  if (!admin) {
    return (
      <FieldGroup label="Where the answer goes">
        <div className="space-y-2">
          {fixed}
          <Switch label="Also send to my Slack DM" checked={draft.dms.includes(me.id)} onChange={(on) => onChange({ ...draft, dms: on ? [me.id] : [] })} />
        </div>
      </FieldGroup>
    );
  }
  const option = (p: Pick<UserDTO, "id" | "name">) => ({ value: p.id, label: p.id === me.id ? `${p.name} (you)` : p.name });
  // Someone already on the list who has since left still shows, so they can be taken off.
  const known = new Set(people.map((p) => p.id));
  const gone = (kind: string) => !people.length ? [] : saved.filter((d) => d.kind === kind && d.userId && !known.has(d.userId)).map((d) => ({ id: d.userId!, name: `${d.userName ?? "Someone"} (left)` }));
  const unlinked = saved.filter((d) => d.kind === "slack_dm" && !d.linked && d.userId && draft.dms.includes(d.userId));
  const channels = draft.channels;
  const setChannel = (i: number, change: Partial<DestinationDraft["channels"][number]>) =>
    onChange({ ...draft, channels: channels.map((c, j) => (j === i ? { ...c, ...change } : c)) });
  return (
    <FieldGroup label="Where the answer goes" hint={ownChat ? "Pandito delivers it when the run succeeds. If it fails, only you are told." : "At least one. Pandito delivers it when the run succeeds; if it fails, its owners are told."}>
      <div className="space-y-3">
        {ownChat && fixed}
        <div>
          <Caption>Slack DM to</Caption>
          <MultiSelect wide search="Search people" placeholder="Nobody" options={[...people, ...gone("slack_dm")].map(option)} selected={draft.dms} onChange={(dms) => onChange({ ...draft, dms })} />
          {unlinked.map((d) => (
            <p key={d.id} className="mt-1.5 text-xs text-warning">{d.userId === me.id ? "You haven't" : `${d.userName ?? "They"} hasn't`} used Slack with Pandito yet: the DM goes once they message Pandito there.</p>
          ))}
        </div>
        <div>
          <Caption>Web chat of</Caption>
          <MultiSelect wide search="Search people" placeholder={ownChat ? "Nobody else" : "Nobody"} options={[...people.filter((p) => !ownChat || p.id !== me.id), ...gone("web")].map(option)} selected={draft.webs} onChange={(webs) => onChange({ ...draft, webs })} />
        </div>
        <div className="space-y-2">
          <Caption>Slack channels</Caption>
          {channels.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className={`${inputCls} font-mono`} aria-label="Channel id" value={c.channel} placeholder="C0123456789" onChange={(e) => setChannel(i, { channel: e.target.value })} />
              <input className={inputCls} aria-label="Label" value={c.label} maxLength={80} placeholder="#product" onChange={(e) => setChannel(i, { label: e.target.value })} />
              <TextLink onClick={() => onChange({ ...draft, channels: channels.filter((_, j) => j !== i) })}>remove</TextLink>
            </div>
          ))}
          {channels.length < CHANNELS_MAX && <TextLink onClick={() => onChange({ ...draft, channels: [...channels, { channel: "", label: "" }] })}>Add a channel</TextLink>}
          <p className="text-xs text-muted">Its id is in the channel's details in Slack. Pandito must be in the channel, and everyone in it reads the answer.</p>
        </div>
      </div>
    </FieldGroup>
  );
}

/** A list row's second line: where else its answer goes, and how the last delivery there went. Nothing when only its owner's chat. */
export function DestinationsLine({ schedule, className = "mt-1" }: { schedule: ScheduleDTO; className?: string }) {
  const me = useUser();
  if (!schedule.destinations.length) return null;
  return (
    <p className={`${className} text-xs text-muted`}>
      {schedule.runsAs === "agent" ? "To" : "Also to"}{" "}
      {schedule.destinations.map((d, i) => {
        const last = deliveryWords(d, timeAgo);
        const bad = d.lastDelivery?.status === "failed" || d.lastDelivery?.status === "skipped";
        return (
          <span key={d.id}>
            {i > 0 && " · "}
            {destinationFor(d, me.id)}
            {last && <span className={bad ? "text-danger" : "text-faint"}> ({last})</span>}
            {d.kind === "slack_dm" && !d.linked && !d.lastDelivery && <span className="text-warning"> (hasn't used Slack with Pandito yet)</span>}
          </span>
        );
      })}
    </p>
  );
}

/** "Run now" on a schedule whose answer reaches other people or a channel: it asks first, naming them. */
export function RunNowDialog({ schedule, busy, onRun, onClose }: { schedule: ScheduleDTO; busy: boolean; onRun: (onlyToMe: boolean) => void; onClose: () => void }) {
  const me = useUser();
  const others = othersOf(schedule).map((d) => destinationFor(d, me.id));
  return (
    <Dialog
      onClose={onClose}
      size="sm"
      title={`Run ${schedule.name} now?`}
      description={`Its answer also goes to ${others.join(", ")}.`}
      busy={busy}
      primary={
        <>
          <Button disabled={busy} onClick={() => onRun(true)}>Only to me</Button>
          <Button variant="primary" disabled={busy} onClick={() => onRun(false)}>Send to all</Button>
        </>
      }
    >
      <p className="text-sm text-muted">
        {schedule.runsAs === "agent"
          ? "Only to me sends its answer to your own web chat and nowhere else: a test before they read it."
          : "Only to me runs it in your own chat and sends it nowhere else: a test before they read it."}
      </p>
    </Dialog>
  );
}
